import {useMutation, useQuery, useQueryClient} from "react-query";
import {DOMAIN_OVERALL_DASHBOARD, EASM_INSIGHTS_DETAILS, EASM_OVERALL_DASHBOARD, DOMAIN_SPECIFIC_DASHBOARD, EASM_EMAIL_CONFIGURATIONS, EASM_ASSETS, EASM_ASSETS_SEARCH, LIST_EASM_MONITORING, LIST_EASM, CREDITS_STATUS, CREDITS_ESTIMATE, ASSET_MANAGEMENT, ASSETS_DETAILS} from "./utils/constants";
import axios from "axios";

export const useDomainValidate = () => {
    return useMutation(
        async (data) => {
            try {
                const response = await axios.post(`/easm/domain/validate`, data);
                return response.data;
            } catch (error) {
                return error.response.data || error;
            }
        }
    );
}

export const useAddDomain = () => {
    const queryClient = useQueryClient()
    return useMutation(async (data) => {
            try {
                const response = await axios.post(`/easm/domain`, data);
                return response.data;
            } catch (error) {
                return error.response.data || error;
            }
        }, {
            onSuccess: async () => {
                await queryClient.invalidateQueries(LIST_EASM_MONITORING)
                await queryClient.invalidateQueries(LIST_EASM)
                // ROAR-5001 (AC1): adding a domain starts an EASM scan, which reserves credits.
                queryClient.invalidateQueries(CREDITS_STATUS)
                // ROAR-5002: the quote carries `available` too; the next Start EASM must not reuse it.
                queryClient.invalidateQueries(CREDITS_ESTIMATE)
            }
        }
    );
}

export const useEASMOverallDashboard = (companyId) => {
    return useQuery(EASM_OVERALL_DASHBOARD, async () => {
        let res = await axios.get(`/easm/overall_dashboard?companyId=${companyId}`)
        return res.data.data;
    })
}

export const useCheckEASMAssetExist = (companyId) => {
    return useQuery(EASM_ASSETS, async () => {
        let res = await axios.get(`/easm/check_asset_exist?companyId=${companyId}`)
        return res?.data?.assetFound;
    })
}

export const useDomainOverallDashboard = (assetId) => {
    return useQuery(DOMAIN_OVERALL_DASHBOARD, async () => {
        let res = await axios.get(`/easm/domain/overall_dashboard?assetId=${assetId}`)
        return res.data.data;
    })
}

export const useDomainSpecificDashboard = (assetId) => {
    return useQuery(DOMAIN_SPECIFIC_DASHBOARD, async () => {
        let res = await axios.get(`/easm/${assetId}/dashboard`)
        return res.data.data;
    })
}

export const useEASMInsightDetails = (companyId, insightId, type = null, assetId = null) => {
    return useQuery([EASM_INSIGHTS_DETAILS, companyId, insightId, type, assetId], async () => {
        let res = await axios.get(`/easm/insight_details?companyId=${companyId}&insightId=${insightId}&type=${type}&assetId=${assetId}`)
        return res.data;
    })
}

export const useEASMEmailConfiguration = (companyId, type) => {
    return useQuery([EASM_EMAIL_CONFIGURATIONS, companyId, type], async () => {
        let res = await axios.get(`/easm/email_configuration?companyId=${companyId}&type=${type}`)
        return res.data;
    })
}

export const useSearchEasmAssets = (query) => {
    const trimmed = (query || '').trim();
    return useQuery([EASM_ASSETS_SEARCH, trimmed], async () => {
        let res = await axios.get(`/easm/assets/search?q=${encodeURIComponent(trimmed)}&limit=20`)
        return res.data
    }, {
        enabled: Boolean(trimmed)
    })
}

export const useStartEasmScan = (assetId) => {
    const queryClient = useQueryClient()
    return useMutation(async () => {
        try {
            const response = await axios.post(`/easm/assets/${assetId}/scan`);
            return response.data;
        } catch (error) {
            return error?.response?.data || error;
        }
    }, {
        onSuccess: async (data) => {
            if (data?.success) {
                await queryClient.invalidateQueries(ASSET_MANAGEMENT)
                await queryClient.invalidateQueries(LIST_EASM_MONITORING)
                // Mirrors useAddDomain: starting a scan reserves credits, so the badge and the
                // next Start EASM quote must not keep showing the pre-scan balance.
                queryClient.invalidateQueries(CREDITS_STATUS)
                queryClient.invalidateQueries(CREDITS_ESTIMATE)
                // ROAR-5051: lets the EASM page's existing easmScanStatus effect pick up
                // 'inprogress' without a hard reload (useGetAssetsInAssetManagement's key).
                queryClient.invalidateQueries(ASSETS_DETAILS)
            }
        }
    })
}

export const useBulkStartEasmScan = () => {
    const queryClient = useQueryClient()
    return useMutation(async (data) => {
        try {
            const response = await axios.post(`/easm/scan`, data);
            return response.data;
        } catch (error) {
            return error?.response?.data || error;
        }
    }, {
        onSuccess: async (data) => {
            if (data?.success) {
                await queryClient.invalidateQueries(ASSET_MANAGEMENT)
                await queryClient.invalidateQueries(LIST_EASM_MONITORING)
                queryClient.invalidateQueries(CREDITS_STATUS)
                queryClient.invalidateQueries(CREDITS_ESTIMATE)
                // ROAR-5051: lets the EASM page's existing easmScanStatus effect pick up
                // 'inprogress' without a hard reload (useGetAssetsInAssetManagement's key).
                queryClient.invalidateQueries(ASSETS_DETAILS)
            }
        }
    })
}

const EASM_SCAN_REASON_LABELS = {
    already_running: 'Already running',
    insufficient_credits: 'Insufficient credits',
    not_eligible_type: 'Not eligible for EASM',
    is_subdomain: 'Covered by parent domain',
    not_found: 'Asset not found',
    dispatch_failed: 'Could not start',
}

// Shared by the search endpoint and the batch-scan endpoint (same reason vocabulary),
// so this one mapping covers both result lists.
export const describeEasmScanReason = (reason) => EASM_SCAN_REASON_LABELS[reason] || 'Could not start'